/**
 * src/data/GatekeepingBank.js
 * Hardcoded scenario bank for Part 4: Time & Gatekeeping
 */

export const gatekeepingBank = [
  {
    "id": "gatekeeping_01",
    "interruptionSource": "Direct Manager via Slack DM",
    "scenarioText": "Your boss texts 'Call me ASAP' while you are 20 minutes away from a hard deadline on an external client pitch deck.",
    "options": ["Do It Now", "Schedule It"],
    "correctAnswer": "Schedule It",
    "rationale": "An external client deliverable takes precedence over an unscheduled internal ping. Acknowledge receipt briefly, state your deadline window, and schedule the callback."
  },
  {
    "id": "gatekeeping_02",
    "interruptionSource": "Automated PagerDuty Alert",
    "scenarioText": "A Severity-1 alert indicates the primary payment gateway is failing globally, while you are leading a routine weekly 1:1 with a direct report.",
    "options": ["Do It Now", "Schedule It"],
    "correctAnswer": "Do It Now",
    "rationale": "Severity-1 operational outages supersede all internal meetings. Halt the 1:1, initiate incident response protocols, and triage the revenue-impacting outage immediately."
  },
  {
    "id": "gatekeeping_03",
    "interruptionSource": "VP of Sales via Email",
    "scenarioText": "The VP sends a high-priority request for a custom data slide for tomorrow's executive pitch, while you are in a scheduled 'Deep Work' block processing today's payroll.",
    "options": ["Do It Now", "Schedule It"],
    "correctAnswer": "Schedule It",
    "rationale": "Payroll is a strict, legally binding operational deadline. The VP's request, while high-level, has a longer buffer (tomorrow) and should be slotted outside of your deep work block."
  },
  {
    "id": "gatekeeping_04",
    "interruptionSource": "Enterprise Client via Direct Email",
    "scenarioText": "A Tier-1 client escalates a major billing error and threatens immediate churn, while you are drafting a routine Q3 internal status report.",
    "options": ["Do It Now", "Schedule It"],
    "correctAnswer": "Do It Now",
    "rationale": "Active churn threats from Tier-1 clients represent immediate revenue risk and override internal, non-urgent documentation tasks."
  },
  {
    "id": "gatekeeping_05",
    "interruptionSource": "HR Business Partner via Teams",
    "scenarioText": "HR escalates a 'Compliance Overdue' notice, demanding you sign the updated employee handbook today. You are currently mitigating a minor server degradation.",
    "options": ["Do It Now", "Schedule It"],
    "correctAnswer": "Schedule It",
    "rationale": "System stability and infrastructure health always override administrative compliance tasks, even when flagged as 'escalated' by internal departments."
  },
  {
    "id": "gatekeeping_06",
    "interruptionSource": "VIP Client via Support Portal",
    "scenarioText": "A VIP client submits a complex feature request marked 'Urgent' during your designated 2-hour strategic planning block.",
    "options": ["Do It Now", "Schedule It"],
    "correctAnswer": "Schedule It",
    "rationale": "Feature requests, even from VIPs, are not operational emergencies. Protect your strategic planning block and triage the request during your next scheduled communication window."
  },
  {
    "id": "gatekeeping_07",
    "interruptionSource": "Legal Counsel via Urgent Call",
    "scenarioText": "Legal flags a severe GDPR data exposure risk in your active code deployment that is currently rolling out to production.",
    "options": ["Do It Now", "Schedule It"],
    "correctAnswer": "Do It Now",
    "rationale": "Active regulatory breaches carry massive enterprise liability. The deployment must be halted and rolled back immediately."
  },
  {
    "id": "gatekeeping_08",
    "interruptionSource": "Direct Report via Slack DM",
    "scenarioText": "A direct report pings you for help with a standard onboarding task while you are actively presenting quarterly numbers to the board.",
    "options": ["Do It Now", "Schedule It"],
    "correctAnswer": "Schedule It",
    "rationale": "Board-level presentations demand absolute focus. Standard onboarding is a low-friction task that must wait until the executive commitment concludes."
  },
  {
    "id": "gatekeeping_09",
    "interruptionSource": "CEO via General Slack Channel",
    "scenarioText": "The CEO posts an open question asking 'Who has thoughts on our new competitor's ad campaign?' during your sprint planning session.",
    "options": ["Do It Now", "Schedule It"],
    "correctAnswer": "Schedule It",
    "rationale": "Avoid the 'urgency trap' of executive squirrel-chasing. An open-ended brainstorming ping should never derail structured, high-value team planning."
  },
  {
    "id": "gatekeeping_10",
    "interruptionSource": "Account Executive via Zoom DM",
    "scenarioText": "An AE requests immediate approval for a 10% pricing discount on a $500k deal that the client is waiting to sign on a live call.",
    "options": ["Do It Now", "Schedule It"],
    "correctAnswer": "Do It Now",
    "rationale": "Live deal negotiations represent point-of-sale revenue capture. Providing a rapid binary approval empowers the sales team to close enterprise revenue."
  },
  {
    "id": "gatekeeping_11",
    "interruptionSource": "AWS Billing Alert",
    "scenarioText": "Your cloud provider flags a 500% spike in API computing costs occurring right now, indicating a potential runaway script.",
    "options": ["Do It Now", "Schedule It"],
    "correctAnswer": "Do It Now",
    "rationale": "Runaway infrastructure costs can drain operational budgets in hours. Immediate intervention is required to kill the anomalous process."
  },
  {
    "id": "gatekeeping_12",
    "interruptionSource": "Marketing Peer via Email",
    "scenarioText": "A peer marks an email 'URGENT: Brainstorming needed' for a campaign launching next month, while you are analyzing critical churn data.",
    "options": ["Do It Now", "Schedule It"],
    "correctAnswer": "Schedule It",
    "rationale": "Misuse of the 'Urgent' tag for non-blocking, future-dated ideation should not pierce deep work protection protocols."
  },
  {
    "id": "gatekeeping_13",
    "interruptionSource": "Physical Office Automated Alert",
    "scenarioText": "You receive an automated alert that the physical office network is completely down, and you are the designated on-call IT lead.",
    "options": ["Do It Now", "Schedule It"],
    "correctAnswer": "Do It Now",
    "rationale": "As the on-call lead, a total network failure halts operational productivity for the entire on-site workforce. Immediate triage is required."
  },
  {
    "id": "gatekeeping_14",
    "interruptionSource": "Talent Acquisition via Teams",
    "scenarioText": "A recruiter asks for a quick 5-minute sync to calibrate a job description while you are prepping for a multi-million dollar client kickoff.",
    "options": ["Do It Now", "Schedule It"],
    "correctAnswer": "Schedule It",
    "rationale": "Client-facing preparation takes absolute precedence over internal administrative alignment. Buffer the request for later in the week."
  },
  {
    "id": "gatekeeping_15",
    "interruptionSource": "PR Director via Slack",
    "scenarioText": "The PR team needs an immediate technical clarification to respond to a negative press article publishing in exactly one hour.",
    "options": ["Do It Now", "Schedule It"],
    "correctAnswer": "Do It Now",
    "rationale": "Impending negative press creates immediate reputational and financial risk. Supplying accurate data before publication is a critical enterprise priority."
  },
  {
    "id": "gatekeeping_16",
    "interruptionSource": "Junior Developer via Slack DM",
    "scenarioText": "A junior developer asks for a quick ad-hoc pairing session to debug a non-critical UI alignment issue.",
    "options": ["Do It Now", "Schedule It"],
    "correctAnswer": "Schedule It",
    "rationale": "Non-blocking cosmetic issues do not warrant synchronous interruption. Instruct them to document the bug and schedule a review during standard office hours."
  },
  {
    "id": "gatekeeping_17",
    "interruptionSource": "Security Information and Event Management (SIEM)",
    "scenarioText": "You notice anomalous, unauthorized data exfiltration in the production environment while conducting routine log reviews.",
    "options": ["Do It Now", "Schedule It"],
    "correctAnswer": "Do It Now",
    "rationale": "Active data breaches are existential threats. Routine work must be instantly abandoned to execute incident response and sever unauthorized access."
  },
  {
    "id": "gatekeeping_18",
    "interruptionSource": "Third-Party Vendor via Email",
    "scenarioText": "A software vendor sends an assertive email asking for a status update on their unpaid invoice, which is 5 days past due.",
    "options": ["Do It Now", "Schedule It"],
    "correctAnswer": "Schedule It",
    "rationale": "While important, an unpaid vendor invoice is a standard accounts payable workflow, not a drop-everything emergency. Route to finance asynchronously."
  },
  {
    "id": "gatekeeping_19",
    "interruptionSource": "CFO via Email",
    "scenarioText": "The CFO sends an urgent request for departmental budget variance justifications, due by next Friday.",
    "options": ["Do It Now", "Schedule It"],
    "correctAnswer": "Schedule It",
    "rationale": "Executive requests with a multi-day buffer are traps for context-switching. Acknowledge receipt, but schedule the analysis for a dedicated time block."
  },
  {
    "id": "gatekeeping_20",
    "interruptionSource": "Auth0 System Alert",
    "scenarioText": "Your third-party authentication integration fails, preventing 100% of global users from logging into the enterprise platform.",
    "options": ["Do It Now", "Schedule It"],
    "correctAnswer": "Do It Now",
    "rationale": "A total lockout of the user base violates all baseline SLAs. Immediate engineering escalation and failover execution are mandatory."
  },
  {
    "id": "gatekeeping_21",
    "interruptionSource": "Team Lead via Slack",
    "scenarioText": "Your team lead asks you to fill out a mandatory quarterly feedback survey by EOD, while you are in a flow state coding a core feature.",
    "options": ["Do It Now", "Schedule It"],
    "correctAnswer": "Schedule It",
    "rationale": "Flow state is highly fragile and valuable. Administrative surveys with an EOD deadline should be pushed to the final 15 minutes of the workday."
  },
  {
    "id": "gatekeeping_22",
    "interruptionSource": "Sales Development Rep (SDR) via Teams",
    "scenarioText": "An SDR requests you jump on an impromptu Zoom to demo the product for a mid-tier prospect who 'wants to see it right now'.",
    "options": ["Do It Now", "Schedule It"],
    "correctAnswer": "Schedule It",
    "rationale": "Impulse demos disrupt operational focus and yield poor conversion rates due to lack of prep. Enforce boundaries and require the SDR to schedule a formal slot."
  },
  {
    "id": "gatekeeping_23",
    "interruptionSource": "CI/CD Pipeline Alert",
    "scenarioText": "Your automated deployment pipeline halts immediately due to a failed critical security integration test on a major release.",
    "options": ["Do It Now", "Schedule It"],
    "correctAnswer": "Do It Now",
    "rationale": "A halted CI/CD pipeline blocks all engineering throughput. Security test failures in deployment require immediate investigation before proceeding."
  },
  {
    "id": "gatekeeping_24",
    "interruptionSource": "Cross-Functional Project Manager via Slack",
    "scenarioText": "A PM pings you for a granular status update on a minor task that is explicitly due next Friday.",
    "options": ["Do It Now", "Schedule It"],
    "correctAnswer": "Schedule It",
    "rationale": "Micromanagement check-ins for future deliverables cause unnecessary friction. Defer the update to the established asynchronous sprint board."
  },
  {
    "id": "gatekeeping_25",
    "interruptionSource": "Cybersecurity Bulletin",
    "scenarioText": "A zero-day vulnerability is announced for an open-source library that is heavily utilized in your core production application.",
    "options": ["Do It Now", "Schedule It"],
    "correctAnswer": "Do It Now",
    "rationale": "Zero-day vulnerabilities in production code leave the enterprise open to immediate, automated exploits. Patching and mitigation take highest priority."
  },
  {
    "id": "gatekeeping_26",
    "interruptionSource": "Internal Stakeholder via Teams",
    "scenarioText": "A non-technical stakeholder asks for a quick 10-minute screenshare tutorial on a new software tool you rolled out yesterday.",
    "options": ["Do It Now", "Schedule It"],
    "correctAnswer": "Schedule It",
    "rationale": "Ad-hoc training requests scale poorly and destroy focus. Direct them to the asynchronous documentation, or schedule a batch training session."
  },
  {
    "id": "gatekeeping_27",
    "interruptionSource": "Database Telemetry Alert",
    "scenarioText": "You receive an automated alert that the primary production database storage is 99% full and will crash in approximately 10 minutes.",
    "options": ["Do It Now", "Schedule It"],
    "correctAnswer": "Do It Now",
    "rationale": "An impending database crash is a catastrophic operational failure. You must immediately expand storage volumes or purge temporary logs."
  },
  {
    "id": "gatekeeping_28",
    "interruptionSource": "Colleague via Email",
    "scenarioText": "A colleague shares a 40-page industry report on 2027 market trends they think you 'really need to read today'.",
    "options": ["Do It Now", "Schedule It"],
    "correctAnswer": "Schedule It",
    "rationale": "Reading broad industry analysis is low-urgency, high-value work. Never consume massive informational payloads on someone else's ad-hoc schedule."
  },
  {
    "id": "gatekeeping_29",
    "interruptionSource": "Key Enterprise Client via Phone",
    "scenarioText": "A key client's project sponsor calls furiously because their entire team's system access was erroneously revoked during a migration.",
    "options": ["Do It Now", "Schedule It"],
    "correctAnswer": "Do It Now",
    "rationale": "Erroneous revocation of client access is an SLA violation and a massive trust breach. Immediate manual restoration is required."
  },
  {
    "id": "gatekeeping_30",
    "interruptionSource": "GitHub Security Scanner",
    "scenarioText": "An alert informs you that a junior engineer accidentally pushed a live AWS production API key to a public repository 3 minutes ago.",
    "options": ["Do It Now", "Schedule It"],
    "correctAnswer": "Do It Now",
    "rationale": "Exposed production credentials on public repositories are scraped by bots in seconds. You must instantly revoke and rotate the compromised keys."
  },
  {
    "id": "gatekeeping_31",
    "interruptionSource": "Facilities Management via Email",
    "scenarioText": "Facilities sends a building-wide notice that the office water supply will be shut off for 30 minutes tomorrow afternoon.",
    "options": ["Do It Now", "Schedule It"],
    "correctAnswer": "Schedule It",
    "rationale": "Logistical FYIs require zero immediate action. Process the information passively and maintain current workflow momentum."
  },
  {
    "id": "gatekeeping_32",
    "interruptionSource": "Sales Director via Slack",
    "scenarioText": "The Sales Director requests an ad-hoc engineering feasibility assessment for a highly customized feature to win a mid-tier prospect.",
    "options": ["Do It Now", "Schedule It"],
    "correctAnswer": "Schedule It",
    "rationale": "Custom technical assessments require deep cognitive overhead. Force sales leadership to submit formal requests for proper sprint planning."
  },
  {
    "id": "gatekeeping_33",
    "interruptionSource": "QA Automation Report",
    "scenarioText": "The QA suite flags a non-blocking cosmetic bug (a slightly misaligned logo) in the staging environment hours before a major release.",
    "options": ["Do It Now", "Schedule It"],
    "correctAnswer": "Schedule It",
    "rationale": "Minor cosmetic bugs are acceptable tech debt during release crunch. Log the defect for a future patch and proceed with the high-impact launch."
  },
  {
    "id": "gatekeeping_34",
    "interruptionSource": "CEO via Urgent Calendar Invite",
    "scenarioText": "The CEO mandates an immediate, company-wide all-hands meeting regarding an unexpected hostile takeover bid.",
    "options": ["Do It Now", "Schedule It"],
    "correctAnswer": "Do It Now",
    "rationale": "Existential corporate events declared by the CEO override all standard operational procedures. Attendance is mandatory and immediate."
  },
  {
    "id": "gatekeeping_35",
    "interruptionSource": "Direct Report via Phone Call",
    "scenarioText": "A direct report calls you visibly distressed, requesting an immediate 1:1 because they are facing a severe personal family emergency.",
    "options": ["Do It Now", "Schedule It"],
    "correctAnswer": "Do It Now",
    "rationale": "Human-centric leadership demands immediate empathetic response to severe personal crises. Workflows can be paused to support team welfare."
  },
  {
    "id": "gatekeeping_36",
    "interruptionSource": "Peer Manager via Slack",
    "scenarioText": "A fellow manager asks for 15 minutes to 'pick your brain' regarding their Q4 roadmap architecture.",
    "options": ["Do It Now", "Schedule It"],
    "correctAnswer": "Schedule It",
    "rationale": "'Pick your brain' requests are unstructured and notorious time-sinks. Require an agenda and schedule the discussion for a low-energy time block."
  },
  {
    "id": "gatekeeping_37",
    "interruptionSource": "Corporate LMS System",
    "scenarioText": "You get an automated email ping that your annual anti-money laundering compliance training is 1 day overdue.",
    "options": ["Do It Now", "Schedule It"],
    "correctAnswer": "Schedule It",
    "rationale": "Automated training compliance, even when slightly overdue, should not interrupt active deep work. Schedule it for the end of the day."
  },
  {
    "id": "gatekeeping_38",
    "interruptionSource": "Chief Legal Officer via Email",
    "scenarioText": "Legal issues an immediate 'Litigation Hold' mandate requiring you to manually halt all automated data deletion scripts on user accounts.",
    "options": ["Do It Now", "Schedule It"],
    "correctAnswer": "Do It Now",
    "rationale": "Failing to execute a litigation hold constitutes spoliation of evidence, carrying severe legal penalties. The automated scripts must be killed instantly."
  },
  {
    "id": "gatekeeping_39",
    "interruptionSource": "Software Vendor via Email",
    "scenarioText": "A vendor offers a limited-time 10% discount if you sign their SaaS renewal contract today, but you haven't reviewed the new legal terms.",
    "options": ["Do It Now", "Schedule It"],
    "correctAnswer": "Schedule It",
    "rationale": "Never sign unreviewed enterprise contracts to secure artificial, high-pressure discounts. Buffer the review and loop in legal and procurement."
  },
  {
    "id": "gatekeeping_40",
    "interruptionSource": "IT Security Dashboard",
    "scenarioText": "You are informed via dashboard that a terminated employee's AWS identity access management (IAM) credentials are still active and logging in.",
    "options": ["Do It Now", "Schedule It"],
    "correctAnswer": "Do It Now",
    "rationale": "Insider threats and zombie credentials from terminated employees pose massive security risks. The IAM access must be permanently revoked immediately."
  },
  {
    "id": "gatekeeping_41",
    "interruptionSource": "Marketing Designer via Slack",
    "scenarioText": "The marketing team urgently requests a logo file format change (PNG to SVG) for a digital campaign launching next Tuesday.",
    "options": ["Do It Now", "Schedule It"],
    "correctAnswer": "Schedule It",
    "rationale": "A launch date next week provides ample buffer. Do not let another department's poor planning dictate your immediate, real-time priorities."
  },
  {
    "id": "gatekeeping_42",
    "interruptionSource": "Network Operations Center (NOC)",
    "scenarioText": "The NOC detects an active Layer-7 DDoS attack attempting to overwhelm your enterprise login infrastructure.",
    "options": ["Do It Now", "Schedule It"],
    "correctAnswer": "Do It Now",
    "rationale": "Active cyberattacks designed to cripple infrastructure require immediate execution of traffic-shaping and firewall mitigation playbooks."
  },
  {
    "id": "gatekeeping_43",
    "interruptionSource": "Peer Colleague via Teams",
    "scenarioText": "A peer asks you to review their slide deck before their presentation tomorrow afternoon, while you are mid-sprint on a critical bug fix.",
    "options": ["Do It Now", "Schedule It"],
    "correctAnswer": "Schedule It",
    "rationale": "Peer reviews are valuable but non-urgent if the deadline is tomorrow. Protect your current sprint velocity and batch the review for later."
  },
  {
    "id": "gatekeeping_44",
    "interruptionSource": "Public Relations Director via Urgent Call",
    "scenarioText": "A major news outlet just published a hit piece containing false, damaging financial data about your company, and PR needs immediate verification.",
    "options": ["Do It Now", "Schedule It"],
    "correctAnswer": "Do It Now",
    "rationale": "Public dissemination of false financial data can tank enterprise valuation in minutes. Providing factual counter-data is a drop-everything priority."
  },
  {
    "id": "gatekeeping_45",
    "interruptionSource": "Office Manager via Slack General",
    "scenarioText": "The office manager tags the channel asking for two volunteers to help organize the catering for the upcoming holiday party.",
    "options": ["Do It Now", "Schedule It"],
    "correctAnswer": "Schedule It",
    "rationale": "Office culture tasks are entirely secondary to operational output. Ignore the ping until your primary deep work cycle is complete."
  },
  {
    "id": "gatekeeping_46",
    "interruptionSource": "VP of Partnerships via Direct Call",
    "scenarioText": "A strategic partner is threatening to walk away from a multi-million dollar integration deal right now due to a technical miscommunication.",
    "options": ["Do It Now", "Schedule It"],
    "correctAnswer": "Do It Now",
    "rationale": "High-stakes executive escalations involving immediate deal collapse require instant technical authority to de-escalate and salvage the partnership."
  },
  {
    "id": "gatekeeping_47",
    "interruptionSource": "C-Suite Executive via Teams",
    "scenarioText": "A C-suite executive pings you with an unscheduled 'cool idea' they want to brainstorm, while you are executing a complex production database push.",
    "options": ["Do It Now", "Schedule It"],
    "correctAnswer": "Schedule It",
    "rationale": "Production deployments require absolute precision. Do not compromise live enterprise systems to entertain executive ideation. Schedule a follow-up."
  },
  {
    "id": "gatekeeping_48",
    "interruptionSource": "Supplier via Legal Notice",
    "scenarioText": "Your primary hardware supplier issues a formal 'Force Majeure' declaration, immediately halting all inbound inventory shipments indefinitely.",
    "options": ["Do It Now", "Schedule It"],
    "correctAnswer": "Do It Now",
    "rationale": "A severed primary supply chain halts the entire business model. You must instantly pivot to operational continuity plans and secondary suppliers."
  },
  {
    "id": "gatekeeping_49",
    "interruptionSource": "OS System Prompt",
    "scenarioText": "Your operating system prompts you to reboot immediately to install a routine enterprise software update.",
    "options": ["Do It Now", "Schedule It"],
    "correctAnswer": "Schedule It",
    "rationale": "Unless mandated by an emergency zero-day patch, routine system updates should be scheduled outside of productive operational hours."
  },
  {
    "id": "gatekeeping_50",
    "interruptionSource": "Endpoint Detection and Response (EDR)",
    "scenarioText": "Your security software alerts you that rapid, unauthorized file encryption (Ransomware) has begun on a shared corporate drive.",
    "options": ["Do It Now", "Schedule It"],
    "correctAnswer": "Do It Now",
    "rationale": "Active ransomware encryption is the most critical operational emergency possible. The network must be immediately air-gapped to prevent lateral spread."
  },
  {
    "id": "gatekeeping_51",
    "interruptionSource": "Direct Manager via SMS at 9:15 PM",
    "scenarioText": "Your boss texts: 'Need the Q3 slides updated ASAP.' You are currently off the clock watching Netflix.",
    "options": ["Do It Now", "Schedule It"],
    "correctAnswer": "Schedule It",
    "rationale": "Unless you are an ER doctor, 'ASAP' at 9:15 PM means 'First thing tomorrow morning.' Protect your downtime to prevent burnout."
  }
];